import { useLayoutEffect, type ReactNode } from 'react'

import { syncShimmer } from '@/lib/motion/skeleton'
import { cn } from '@/lib/utils'

/**
 * A placeholder block: the raised surface with a highlight sweeping across
 * it (`skeleton` in globals.css). Size and shape come from `className`.
 *
 * It does not time itself. A route's pending page holds its blocks back for
 * 80ms (ScreenLayout's `pending`), and a card's loading state does the same
 * through useSkeletonTiming, so a block drawn in either never flashes grey.
 * It only joins the shared shimmer clock as it mounts (`syncShimmer`).
 * `appear` gives a block drawn on its own, outside those, the same 80ms wait
 * and fade; for several blocks at once, `SkeletonGroup` (components/motion).
 */
export default function Skeleton({
  className,
  appear = false,
}: {
  className?: string
  appear?: boolean
}) {
  useLayoutEffect(syncShimmer, [])
  return (
    <div
      className={cn('skeleton rounded-xs bg-skeleton', appear && 'animate-skeleton-in', className)}
      aria-hidden="true"
    />
  )
}

/**
 * The box a card's skeleton is drawn in, inside the root that later holds
 * its content too (CardState, QueryState; see useSkeletonTiming).
 *
 * While loading it has no box of its own (`display: contents`), so the
 * blocks are laid out by the root, as they were before this wrapper
 * existed. Once the content lands it stays mounted for the swap, `leaving`:
 * laid over the content with the root's own layout, fading out, hidden from
 * assistive tech and out of reach of the pointer and the keyboard. It is
 * the same element throughout, so the blocks in it are never remounted and
 * their shimmer never restarts.
 */
export function SkeletonLayer({ leaving, children }: { leaving: boolean; children: ReactNode }) {
  return (
    <div
      data-skeleton-layer
      aria-hidden={leaving || undefined}
      inert={leaving}
      className={leaving ? 'skeleton-leaving' : 'contents'}
    >
      {children}
    </div>
  )
}
